import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import type { AuthUser } from '@/store/auth.store';

export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  accessToken: string;
  user: AuthUser;
}

// Calls /auth/refresh then /auth/me with raw fetch so we don't enter the
// api.ts 401→refresh→retry loop during the bootstrap phase.
// Returns the AuthUser on success, null when there is no valid session.
// staleTime: Infinity — session state is managed via mutations, not polling.
export function useSessionQuery() {
  const { setAuth } = useAuthStore();

  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const refreshRes = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (!refreshRes.ok) return null;

        const { accessToken } = (await refreshRes.json()) as { accessToken: string };

        const meRes = await fetch('/api/v1/auth/me', {
          credentials: 'include',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!meRes.ok) return null;

        const user = (await meRes.json()) as AuthUser;
        setAuth(accessToken, user);
        return user;
      } catch {
        return null;
      }
    },
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      api.post<LoginResponse>('/auth/login', credentials),
    onSuccess: ({ accessToken, user }) => {
      setAuth(accessToken, user);
      qc.setQueryData<AuthUser | null>(SESSION_QUERY_KEY, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSettled: () => {
      clearAuth();
      qc.setQueryData(SESSION_QUERY_KEY, null);
      navigate('/login', { replace: true });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: { email: string }) =>
      api.post<{ resetToken?: string }>('/auth/forgot-password', data),
  });
}
